import React, { useEffect, useRef, useState, forwardRef } from "react";
import { useSelector } from "react-redux";
import classNames from "classnames";
import { Modifiers } from "popper.js";
import { Button, Col, Container, Row } from "Components/ReactstrapSlim";
import * as Icons from "Components/Icons";
import useMediaBreakpointDown from "Hooks/useMediaBreakpointDown";
import { brandingSelector, userSelector } from "Utils/selectors";
import { BrandingCustomCss } from "./BrandingCustomCss";
import { CollapsibleSearch, CollapsibleSearchMode } from "./CollapsibleSearch";
import { HeaderSuperUser } from "./HeaderSuperUser";
import { HeaderLeft } from "./HeaderLeft";
import { HeaderRight } from "./HeaderRight";
import { HeaderSearch } from "./HeaderSearch";
import { HeaderUserDropDown } from "./HeaderUserDropDown";

import classes from "./Header.module.scss"

export interface HeaderProps {
   redirectToMyLiquidSpaceAfterSignIn?: boolean;
   className?: string;
   fluid?: boolean | string;
   noSearch?: boolean;
   isEnterpriseManagment?: boolean;
   dispatchMobileToggle?: boolean;
}

export const modifiers: Modifiers = {
   offset: { enabled: true, offset: "0,0" },
   preventOverflow: {
      enabled: true,
      padding: {
         left: 0
      }
   },
   flip: {
      enabled: false
   }
};

const Header = forwardRef<HTMLElement, HeaderProps>(({ className, fluid, noSearch, redirectToMyLiquidSpaceAfterSignIn, isEnterpriseManagment, dispatchMobileToggle }, ref) => {
   const user = useSelector(userSelector);
   const isMobile = useMediaBreakpointDown("sm");
   const [searchMode, setSearchMode] = useState(CollapsibleSearchMode.Collapsed);

   const branding = useSelector(brandingSelector);

   const placeholderElementRef = useRef<HTMLDivElement | null>(null);
   const sidenavTogglesClassName = "sidenav-toggled";

   const sidebarToggle = () => {
      document.body.classList.toggle(sidenavTogglesClassName);
   };

   const sidebarClose = () => {
      if (isEnterpriseManagment) {
         document.body.classList.remove(sidenavTogglesClassName);
      }
   };

   useEffect(() => {
      const stickyElement = document.getElementById("header-wrapper");
      if (placeholderElementRef.current && stickyElement && window.IntersectionObserver) {
         stickyElement.classList.add("header-wrapper--collapsible");
         if (!!branding && !noSearch) {
            stickyElement.classList.add("header-wrapper--branded-with-search");
         }

         const intersectionObserver = new IntersectionObserver((entries, observer) => {
            window.requestAnimationFrame(() => {
               entries.forEach(e => {
                  stickyElement.classList.toggle("header-wrapper--collapsed", !e.isIntersecting)
               });
            });
         }, {
            root: null,
            rootMargin: "0px",
            threshold: 0
         });

         intersectionObserver.observe(placeholderElementRef.current);

         return function clear() {
            placeholderElementRef.current && intersectionObserver.unobserve(placeholderElementRef.current);
         }
      }
   }, []);


   const [mobileUserMenuPortalElement, setMobileUserMenuPortalElement] = useState<HTMLDivElement | null>(null);

   return <>
      <header
         className={classNames("header", classes.headerContainer, { "header--search": searchMode == CollapsibleSearchMode.Expanded, "header--branded": !!branding, "header--superuser": user.isSuperUser }, className)}
         ref={ref}
      >
         <BrandingCustomCss />
         <Container fluid={fluid}>
            <Row className="header-row">
               {isEnterpriseManagment &&
                  <Button color="transparent-dark" className="header-user-toggle btn btn-secondary d-lg-none" id="sidebarToggle" onClick={sidebarToggle}>
                     <Icons.Menu className="feather" />
                  </Button>
               }
               <HeaderLeft isSuperUser={user.isSuperUser} isAuthenticated={user.isAuthenticated} isEnterpriseManagment={isEnterpriseManagment} className={classNames("col col-auto", { "col-md-4": !!branding }, { "col-4": !user.isSuperUser && isEnterpriseManagment && !!branding && !branding.logoUrl })} />
               {
                  user.isSuperUser
                     ? <>
                        <div className="header-expander"></div>
                        {isEnterpriseManagment && isMobile
                           ? <></>
                           : <CollapsibleSearch mode={searchMode} onModeChange={mode => setSearchMode(mode)} />}
                     </>
                     : branding
                        ? noSearch
                           ? <div className="header-expander"></div>
                           : <>

                              <HeaderSearch
                                 className="col-12 col-md header-search-branded header-search"
                                 onButtonClick={e => { /* empty */ }}
                                 ariaLabel="find"
                                 buttonContent={() => <Icons.Find />} />
                              <div className="header-expander"></div>
                           </>
                        : noSearch
                           ? <>
                              <div className="header-expander"></div>
                              <HeaderRight className="col col-auto" />
                           </>
                           : <>
                              <div className="header-expander"></div>

                              <HeaderRight className="col col-auto" />
                              <CollapsibleSearch mode={searchMode} onModeChange={mode => setSearchMode(mode)} />
                           </>
               }
               {
                  user.isSuperUser
                     ?
                     <Col className="col-auto header-su">
                        <HeaderSuperUser onOpen={sidebarClose} isEnterpriseManagment={isEnterpriseManagment} />
                     </Col>
                     : null
               }

               <Col className="col-auto header-user">
                  {
                     mobileUserMenuPortalElement
                        ? <HeaderUserDropDown
                           onOpen={sidebarClose}
                           redirectToMyLiquidSpaceAfterSignIn={redirectToMyLiquidSpaceAfterSignIn}
                           dispatchMobileToggle={dispatchMobileToggle}
                           isEnterpriseManagment={isEnterpriseManagment}
                           mobileUserMenuPortalElement={mobileUserMenuPortalElement}
                        />
                        : null
                  }
               </Col>
            </Row>
         </Container>
      </header>
      <div ref={placeholderElementRef} className={classNames("header-placeholder", { "header-placeholder--branded": !!branding, "header-placeholder--superuser": user.isSuperUser })}></div>
      <div ref={e => setMobileUserMenuPortalElement(e)}></div>
   </>;
});

export default Header;